/**
 * Follow-up scheduling for the survey.
 *
 * Two automated nudges, timed from the original invite, sent only to people who
 * have NOT completed the survey. Deliberately not "has not opened": on the live
 * campaign 4 of the 7 completers were never recorded as having opened the email,
 * because Apple Mail and Gmail suppress the tracking pixel — an opens-based rule
 * would nudge people to redo a survey they had already finished.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Skeleton,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import apiService from '../../services/api';
import { usePermissionAccess } from '../auth/RoleGuard';

const TEAL = '#1A5F7A';

export interface FollowUpSettingsValues {
  enabled: boolean;
  followup1_hours: number;
  followup2_hours: number;
  send_window_start: number;
  send_window_end: number;
  max_sends_per_run: number;
  updated_at?: string | null;
  updated_by?: string | null;
}

/** Form state is string-typed on purpose — see the note on coercion below. */
type Draft = {
  enabled: boolean;
  followup1_hours: string;
  followup2_hours: string;
  max_sends_per_run: string;
};

const toDraft = (s: FollowUpSettingsValues): Draft => ({
  enabled: Boolean(s.enabled),
  followup1_hours: String(s.followup1_hours),
  followup2_hours: String(s.followup2_hours),
  max_sends_per_run: String(s.max_sends_per_run),
});

/**
 * Guarded coercion. `parseInt(e.target.value)` yields NaN the moment the field is
 * cleared, which is a live bug in Settings.tsx and EmailTriggers.tsx — the value
 * silently becomes NaN and the Save button stays enabled.
 */
const num = (v: string): number | null => {
  const t = v.trim();
  if (t === '' || !/^\d+$/.test(t)) return null;
  return Number(t);
};

const describeHours = (v: string): string => {
  const n = num(v);
  if (n === null) return 'Whole hours, 1–336.';
  if (n < 24) return `${n} hour${n === 1 ? '' : 's'}.`;
  const days = n / 24;
  return `${n} hours — ${Number.isInteger(days) ? days : days.toFixed(1)} day${days === 1 ? '' : 's'}.`;
};

interface Props {
  /** Bumped by the parent after a sync so the preview re-reads. */
  refreshKey?: number;
  /** Which survey's schedule this is. Each campaign has its own. */
  campaign?: string | null;
}

const FollowUpSettings: React.FC<Props> = ({ refreshKey = 0, campaign = null }) => {
  const { hasFullAccess } = usePermissionAccess();
  // A view_only admin can open this page, so without this they would fill the
  // form and then eat a 403 from the API on Save.
  const canEdit = hasFullAccess('audience_manager');

  const [baseline, setBaseline] = useState<FollowUpSettingsValues | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [preview, setPreview] = useState<{ followup1: number; followup2: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState<{ severity: 'success' | 'error' | 'info'; text: string } | null>(
    null
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [settingsRes, previewRes] = await Promise.all([
        apiService.getSurveySettings(campaign || undefined),
        apiService.previewSurveyFollowups(campaign || undefined).catch(() => null),
      ]);
      const s: FollowUpSettingsValues = settingsRes?.settings;
      setBaseline(s);
      setDraft(toDraft(s));
      if (previewRes?.preview?.eligible) setPreview(previewRes.preview.eligible);
    } catch (e: any) {
      setNote({
        severity: 'error',
        text:
          e?.message === 'Unauthorized'
            ? 'Your session expired. Reload the page to sign in again.'
            : e?.message || 'Could not load the follow-up settings.',
      });
    }
    setLoading(false);
  }, [campaign]);

  useEffect(() => {
    if (campaign) load();
  }, [load, refreshKey, campaign]);

  const h1 = draft ? num(draft.followup1_hours) : null;
  const h2 = draft ? num(draft.followup2_hours) : null;
  const cap = draft ? num(draft.max_sends_per_run) : null;

  // Mirrors the server-side bounds so Save is only offered for a body the API
  // will accept. The API still re-validates.
  const errors = useMemo(() => {
    const e: Record<string, string> = {};
    if (h1 === null || h1 < 1 || h1 > 336) e.followup1_hours = 'Between 1 and 336 hours.';
    if (h2 === null || h2 < 1 || h2 > 336) e.followup2_hours = 'Between 1 and 336 hours.';
    if (h1 !== null && h2 !== null && h2 <= h1) {
      e.followup2_hours = 'Must be later than the first follow-up.';
    }
    if (cap === null || cap < 1 || cap > 200) e.max_sends_per_run = 'Between 1 and 200.';
    return e;
  }, [h1, h2, cap]);

  const valid = Object.keys(errors).length === 0;
  const dirty = useMemo(() => {
    if (!draft || !baseline) return false;
    return (
      draft.enabled !== Boolean(baseline.enabled) ||
      num(draft.followup1_hours) !== baseline.followup1_hours ||
      num(draft.followup2_hours) !== baseline.followup2_hours ||
      num(draft.max_sends_per_run) !== baseline.max_sends_per_run
    );
  }, [draft, baseline]);

  const save = async () => {
    if (!draft || !baseline || !valid) return;
    setSaving(true);
    setNote(null);
    try {
      const res = await apiService.updateSurveySettings(
        {
          ...baseline,
          enabled: draft.enabled,
          followup1_hours: h1,
          followup2_hours: h2,
          max_sends_per_run: cap,
        },
        campaign || undefined
      );
      const s: FollowUpSettingsValues = res?.settings;
      setBaseline(s);
      setDraft(toDraft(s));
      setNote({
        severity: 'success',
        text: s.enabled
          ? 'Saved. Follow-ups are ON — the next scheduled run will start sending.'
          : 'Saved. Follow-ups are OFF — nothing will send.',
      });
    } catch (e: any) {
      setNote({
        severity: 'error',
        text:
          e?.message?.includes('PERMISSION') || e?.message?.includes('403')
            ? 'You have view-only access to this area, so this cannot be changed.'
            : e?.message || 'Could not save.',
      });
    }
    setSaving(false);
  };

  const set = (k: keyof Draft) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setDraft((d) => (d ? { ...d, [k]: e.target.value } : d));

  return (
    <Paper sx={{ p: 2.5, borderRadius: '14px', border: '1px solid #e5e7eb', mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography
          sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '13px', fontWeight: 700, color: '#111827' }}
        >
          Follow-up emails
        </Typography>
        {baseline && (
          <Chip
            size="small"
            label={baseline.enabled ? 'On' : 'Off'}
            sx={{
              height: 22,
              fontSize: 11,
              fontWeight: 700,
              bgcolor: baseline.enabled ? '#DCFCE7' : '#F3F4F6',
              color: baseline.enabled ? '#166534' : '#6B7280',
            }}
          />
        )}
      </Box>

      <Typography sx={{ fontSize: '12px', color: '#6B7280', mb: 2 }}>
        Two reminders, timed from the original invite, sent only to members who have
        not completed the survey. Anyone who has answered, bounced or unsubscribed is
        never contacted again.
      </Typography>

      {note && (
        <Alert severity={note.severity} onClose={() => setNote(null)} sx={{ mb: 2, fontSize: 13 }}>
          {note.text}
        </Alert>
      )}

      {loading || !draft ? (
        <Skeleton variant="rectangular" height={150} sx={{ borderRadius: 2 }} />
      ) : (
        <>
          {/*
            Given its own bordered, colour-shifting row. As a bare Switch this was
            mistaken for decoration and not recognised as the on/off control at all —
            an unchecked MUI switch is a pale grey pill that disappears next to bold
            text. The border and background make it read as a thing you operate.
          */}
          <Box
            onClick={
              canEdit ? () => setDraft({ ...draft, enabled: !draft.enabled }) : undefined
            }
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              mb: 2.5,
              px: 2,
              py: 1.5,
              borderRadius: '10px',
              border: '1px solid',
              borderColor: draft.enabled ? '#86EFAC' : '#E5E7EB',
              bgcolor: draft.enabled ? '#F0FDF4' : '#F9FAFB',
              cursor: canEdit ? 'pointer' : 'default',
              transition: 'background-color .15s, border-color .15s',
              '&:hover': canEdit ? { borderColor: draft.enabled ? '#4ADE80' : '#D1D5DB' } : {},
            }}
          >
            <Switch
              checked={draft.enabled}
              disabled={!canEdit}
              // Click is handled by the row, so stop it bubbling back and toggling twice.
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })}
              inputProps={{ 'aria-label': 'Enable survey follow-up emails' }}
              sx={{
                // `.Mui-checked` alone does not reach the thumb — the class sits on
                // .MuiSwitch-switchBase. Without this the ON state stayed default
                // purple instead of brand teal.
                '& .MuiSwitch-switchBase.Mui-checked': { color: TEAL },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: TEAL,
                  opacity: 0.55,
                },
              }}
            />
            <Box sx={{ flexGrow: 1 }}>
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>
                {draft.enabled ? 'Follow-ups are ON' : 'Follow-ups are OFF'}
              </Typography>
              <Typography sx={{ fontSize: 12, color: '#6B7280' }}>
                {draft.enabled
                  ? 'Reminders will send automatically on the schedule below.'
                  : 'Nothing will send. Turn this on, then Save, to start the reminders.'}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
            <TextField
              size="small"
              type="number"
              label="First follow-up"
              value={draft.followup1_hours}
              onChange={set('followup1_hours')}
              disabled={!canEdit}
              error={Boolean(errors.followup1_hours)}
              helperText={errors.followup1_hours || describeHours(draft.followup1_hours)}
              inputProps={{ min: 1, max: 336, step: 1, 'aria-label': 'Hours after invite' }}
              sx={{ width: 210 }}
            />
            <TextField
              size="small"
              type="number"
              label="Second follow-up"
              value={draft.followup2_hours}
              onChange={set('followup2_hours')}
              disabled={!canEdit}
              error={Boolean(errors.followup2_hours)}
              helperText={errors.followup2_hours || describeHours(draft.followup2_hours)}
              inputProps={{ min: 1, max: 336, step: 1, 'aria-label': 'Hours after invite' }}
              sx={{ width: 210 }}
            />
            <TextField
              size="small"
              type="number"
              label="Max sends per run"
              value={draft.max_sends_per_run}
              onChange={set('max_sends_per_run')}
              disabled={!canEdit}
              error={Boolean(errors.max_sends_per_run)}
              helperText={errors.max_sends_per_run || 'Cap per 15-minute run.'}
              inputProps={{ min: 1, max: 200, step: 1, 'aria-label': 'Max sends per run' }}
              sx={{ width: 190 }}
            />
          </Box>

          <Typography sx={{ fontSize: 12, color: '#6B7280', mb: 2 }}>
            Both are measured from the invite, so the second is the total elapsed time,
            not the gap after the first. Sends are held to{' '}
            <strong>
              {baseline?.send_window_start ?? 9}:00–{baseline?.send_window_end ?? 18}:00 Mountain
            </strong>{' '}
            so nobody is emailed overnight.
          </Typography>

          {preview && (
            <Alert severity="info" icon={false} sx={{ mb: 2, fontSize: 13, bgcolor: '#F0F9FF' }}>
              Right now <strong>{preview.followup1}</strong>{' '}
              {preview.followup1 === 1 ? 'member is' : 'members are'} due the first follow-up and{' '}
              <strong>{preview.followup2}</strong> the second.
              {!draft.enabled && ' Nothing sends while the switch above is off.'}
            </Alert>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              variant="contained"
              onClick={save}
              disabled={!canEdit || !dirty || !valid || saving}
              sx={{ textTransform: 'none', bgcolor: TEAL, '&:hover': { bgcolor: '#14506B' } }}
            >
              {saving ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Save'}
            </Button>
            {dirty && !saving && (
              <Button
                onClick={() => baseline && setDraft(toDraft(baseline))}
                sx={{ textTransform: 'none', color: '#6B7280' }}
              >
                Revert
              </Button>
            )}
            {!canEdit && (
              <Typography sx={{ fontSize: 12, color: '#9CA3AF' }}>
                You have view-only access to this area.
              </Typography>
            )}
            {canEdit && baseline?.updated_at && !dirty && (
              <Typography sx={{ fontSize: 12, color: '#9CA3AF' }}>
                Last changed {new Date(baseline.updated_at).toLocaleString()}
                {baseline.updated_by ? ` by ${baseline.updated_by}` : ''}
              </Typography>
            )}
          </Box>
        </>
      )}
    </Paper>
  );
};

export default FollowUpSettings;
